import { useMutation, useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty, difference } from 'lodash'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useParams, useNavigate } from 'react-router-dom'

import { Content, ContentHeader } from 'components/v2/Content'
import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import { NewUser } from 'features/Users/components/NewUser'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canWrite, getPermissionIDsByRoles } from 'features/Users/utils'
import { getDifference } from 'utils/object'
import { useLogin } from 'features/Login/hooks'
import { INewUserValues } from 'features/Users/types'
import { GTUser } from 'gql/types.generated'
import { EDIT_USER } from 'features/Users/mutations-legacy'
import { GET_USER } from 'features/Users/queries-legacy'

const messages = defineMessages({
  helmet: {
    id: 'users.edit.helmet',
    defaultMessage: 'Edit User | Nexbus Admin'
  }
})

const EditUserView = (): ReactElement => {
  let content
  const navigate = useNavigate()
  const { userId } = useParams()
  const intl = useIntl()
  const login = useLogin()

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery<{ user: GTUser }>(GET_USER, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      userId: userId
    }
  })

  const [editUser, { loading: mutationLoading, error: mutationError }] = useMutation(EDIT_USER, {
    onCompleted() {
      navigate('/users')
    }
  })

  const handleSubmit = (user: INewUserValues): void => {
    const previousUser = {
      firstName: queryData?.user.firstName,
      lastName: queryData?.user.lastName,
      email: queryData?.user.email,
      position: queryData?.user.position,
      policyIDs: [
        ...(queryData?.user.roles?.map(({ id }): string => id) || []),
        ...(queryData?.user.permissions?.map(({ id }): string => id) || [])
      ],
      companyIDs: queryData?.user.companies?.map(({ id }): string => id) || [],
      mfaEnabled: queryData?.user.mfaEnabled,
      passwordExpirationEnabled: queryData?.user.passwordExpirationEnabled
    }

    const newRolesPermissionIds = getPermissionIDsByRoles(user.roles)

    const newUser = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      position: user.position,
      policyIDs: [...user.roles.map(({ id }): string => id), ...difference(user.permissionIDs, newRolesPermissionIds)],
      companyIDs: user.companies.map(({ id }): string => id),
      mfaEnabled: user.mfaEnabled,
      passwordExpirationEnabled: user.passwordExpirationEnabled
    }
    const payload = getDifference(newUser, previousUser)

    if (isEmpty(payload)) {
      navigate('/users')
    } else {
      editUser({
        variables: {
          user: {
            id: userId,
            ...payload
          }
        }
      })
    }
  }

  useEffect(() => {
    refetch()
  }, [])

  if (!canWrite(FEATURES.USER, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.USER} />
  }

  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="users.edit.error.description"
              defaultMessage="There was an error while loading your user"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button onClick={(): TSFixMe => refetch()} kind="primary">
              <FormattedMessage id="users.edit.error.action" defaultMessage="Reload your user" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    content = (
      <NewUser onSubmit={handleSubmit} user={queryData?.user} loading={mutationLoading} error={mutationError} isEdit />
    )
  }

  return (
    <Content hasMaxWidth>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="users.edit.title" defaultMessage="Edit user" />
        </Heading.H3>
      </ContentHeader>
      <section>
        <Card.Container>{content}</Card.Container>
      </section>
    </Content>
  )
}

export default withLastUrl(EditUserView)
